/**
 * Share a post-it with users (read or edit).
 */
import * as React from 'react';
import { PostIt, PostItShare } from '@/types/post-it.types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox as ShadcnCheckbox } from '@/components/ui/checkbox';
import { Button } from '@/components/forms/Button';
import { SearchInput } from '@/components/forms/SearchInput';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { X, Check, Share2, ChevronLeft, ChevronRight, Loader2, BookOpen, Edit } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getUsersForSharing } from '@/services/users.service';
import { getAllowedTargetRoles } from '@/services/post-its.service';
import type { UserWithProfile } from '@/types/user.types';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { useMode } from '@/contexts/ModeContext';
import {
  dedupeShareableRoles,
  filterNewShareUserIds,
  getShareUserInitials,
  isShareRoleFilterValid,
  mergePageSelection,
  mergeSelectedUsersMap,
  resolveShareRoleCodes,
  toggleSelectedUserId,
} from '@/components/post-its/post-it-form-utils';

/** Props for {@link ShareModal}. */
interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  postIt: PostIt;
  onShareUsers: (userIds: string[], permission: 'read' | 'write') => Promise<void>;
  onShareRole: (roleCode: string, permission: 'read' | 'write') => Promise<void>;
  onRemoveShare: (userId: string) => Promise<void>;
  /** When true, selection is only confirmed locally — share applies on post-it save */
  deferUntilSave?: boolean;
  initialSelection?: { userIds: string[]; permission: 'read' | 'write' };
  /** Existing shares (avoids duplicates); overrides postIt.shares when provided */
  existingShares?: PostItShare[];
}

/** Share a post-it with users (read or edit). */
export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  postIt,
  onShareUsers,
  onShareRole: _onShareRole,
  onRemoveShare,
  deferUntilSave = false,
  initialSelection,
  existingShares,
}) => {
  const { mode: appMode } = useMode();

  const [userSearch, setUserSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [selectedUsers, setSelectedUsers] = React.useState<string[]>([]);
  const [selectedUsersMap, setSelectedUsersMap] = React.useState<Record<string, UserWithProfile>>(
    {},
  );
  const [permission, setPermission] = React.useState<'read' | 'write'>('read');
  const [isSharing, setIsSharing] = React.useState(false);
  /** 'all' or a role code from allowed-target-roles (same source as assigned post-its) */
  const [roleFilter, setRoleFilter] = React.useState<string>('all');
  const [page, setPage] = React.useState(1);
  const pageSize = 10;

  const shares = existingShares ?? postIt.shares ?? [];
  const alreadySharedIds = React.useMemo(() => shares.map((s) => s.userId), [shares]);

  // Same roles as assigned post-its (hierarchy + in-person / remote mode)
  const { data: allowedRoles = [] } = useQuery({
    queryKey: ['allowed-target-roles', appMode],
    queryFn: () => getAllowedTargetRoles(appMode ?? undefined),
    enabled: isOpen && !!appMode,
  });

  const shareableRoles = React.useMemo(() => dedupeShareableRoles(allowedRoles), [allowedRoles]);

  const allShareableCodes = React.useMemo(
    () => shareableRoles.map((r) => r.code),
    [shareableRoles],
  );

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(userSearch);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch]);

  // Reset filter if role left the list after mode change
  React.useEffect(() => {
    if (roleFilter === 'all') return;
    if (!isShareRoleFilterValid(roleFilter, shareableRoles)) {
      setRoleFilter('all');
      setPage(1);
    }
  }, [shareableRoles, roleFilter]);

  const roleCodes = React.useMemo(
    () => resolveShareRoleCodes(roleFilter, allShareableCodes),
    [roleFilter, allShareableCodes],
  );

  const { data: usersResponse, isLoading } = useQuery({
    queryKey: [
      'users-for-sharing',
      debouncedSearch,
      page,
      pageSize,
      roleCodes,
      alreadySharedIds,
      appMode,
    ],
    queryFn: () =>
      getUsersForSharing({
        search: debouncedSearch || undefined,
        page,
        pageSize,
        roleCodes,
        excludeIds: alreadySharedIds,
      }),
    enabled: isOpen && roleCodes.length > 0,
  });

  const users = usersResponse?.data || [];
  const total = usersResponse?.total || 0;
  const totalPages = Math.ceil(total / pageSize) || 1;

  React.useEffect(() => {
    if (!users.length) return;
    setSelectedUsersMap((prev) => mergeSelectedUsersMap(prev, users, selectedUsers));
  }, [users, selectedUsers]);

  const selectedUserObjects = React.useMemo(
    () =>
      selectedUsers
        .map((id) => selectedUsersMap[id])
        .filter((u): u is UserWithProfile => Boolean(u)),
    [selectedUsers, selectedUsersMap],
  );

  React.useEffect(() => {
    if (!isOpen) return;
    setSelectedUsers(
      (initialSelection?.userIds ?? []).filter((id) => !alreadySharedIds.includes(id)),
    );
    setUserSearch('');
    setDebouncedSearch('');
    setPage(1);
    setRoleFilter('all');
    setPermission(initialSelection?.permission ?? 'read');
    // Reset only when the modal opens (not when shares finish loading).
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen || alreadySharedIds.length === 0) return;
    setSelectedUsers((prev) => prev.filter((id) => !alreadySharedIds.includes(id)));
  }, [alreadySharedIds, isOpen]);

  /** Share selected users, or only stage them when `deferUntilSave`. */
  const handleShareUsers = async () => {
    const uniqueNewIds = filterNewShareUserIds(selectedUsers, alreadySharedIds);

    if (uniqueNewIds.length === 0 && !deferUntilSave) {
      toast.error(getErrorMessage('noUserSelected'));
      return;
    }

    setIsSharing(true);
    try {
      await onShareUsers(uniqueNewIds, permission);
      if (!deferUntilSave) {
        toast.success(`Post-it partagé avec ${uniqueNewIds.length} utilisateur(s)`);
        setSelectedUsers([]);
        setSelectedUsersMap({});
        setUserSearch('');
        setDebouncedSearch('');
        setPermission('read');
      }
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error));
      if (import.meta.env.DEV) {
        console.error('Error sharing post-it:', error);
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleRemoveShare = async (userId: string) => {
    try {
      await onRemoveShare(userId);
      toast.success('Accès retiré avec succès');
    } catch (error) {
      toast.error(getErrorMessage(error));
      if (import.meta.env.DEV) {
        console.error('Error removing share:', error);
      }
    }
  };

  const toggleUserSelection = (user: UserWithProfile) => {
    if (alreadySharedIds.includes(user.id)) return;
    setSelectedUsers((prev) => {
      if (prev.includes(user.id)) return toggleSelectedUserId(prev, user.id);
      setSelectedUsersMap((map) => ({ ...map, [user.id]: user }));
      return toggleSelectedUserId(prev, user.id);
    });
  };

  const removeSelectedUser = (userId: string) => {
    setSelectedUsers((prev) => prev.filter((id) => id !== userId));
  };

  /** Toggle every selectable user on the current search page. */
  const handleSelectAll = () => {
    const selectable = users.filter((u) => !alreadySharedIds.includes(u.id));
    const pageIds = selectable.map((u) => u.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedUsers.includes(id));

    if (allSelected) {
      setSelectedUsers((prev) => mergePageSelection(prev, pageIds, true));
    } else {
      setSelectedUsersMap((map) => {
        const next = { ...map };
        for (const u of selectable) next[u.id] = u;
        return next;
      });
      setSelectedUsers((prev) => mergePageSelection(prev, pageIds, false));
    }
  };

  const getInitials = getShareUserInitials;

  const canRemoveExisting = Boolean(postIt.id);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'max-w-2xl max-h-[90vh] overflow-hidden gap-0 p-0',
          'border border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)]',
        )}
      >
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-3 pr-12">
          <DialogTitle className="flex items-center gap-2 text-[var(--color-1-nuance-1)]">
            <Share2 className="w-5 h-5 text-[var(--color-2-nuance-1)]" />
            {deferUntilSave ? `Destinataires — "${postIt.title}"` : `Partager "${postIt.title}"`}
          </DialogTitle>
          {deferUntilSave && (
            <p className="text-sm text-[var(--color-1-nuance-2)] text-left">
              La sélection sera appliquée uniquement à la validation du post-it.
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 space-y-5 pb-4">
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => {
                setRoleFilter('all');
                setPage(1);
              }}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200',
                roleFilter === 'all'
                  ? 'bg-[var(--color-2-nuance-1)] text-white'
                  : 'bg-[color-mix(in_srgb,var(--color-1-nuance-3)_10%,transparent)] text-[var(--color-1-nuance-1)] hover:bg-[color-mix(in_srgb,var(--color-1-nuance-3)_20%,transparent)]',
              )}
            >
              Tous
            </button>
            {shareableRoles.map((role) => (
              <button
                key={role.id}
                type="button"
                onClick={() => {
                  setRoleFilter(role.code);
                  setPage(1);
                }}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200',
                  roleFilter.toLowerCase() === role.code.toLowerCase()
                    ? 'bg-[var(--color-2-nuance-1)] text-white'
                    : 'bg-[color-mix(in_srgb,var(--color-1-nuance-3)_10%,transparent)] text-[var(--color-1-nuance-1)] hover:bg-[color-mix(in_srgb,var(--color-1-nuance-3)_20%,transparent)]',
                )}
              >
                {role.label}
              </button>
            ))}
          </div>

          {selectedUserObjects.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedUserObjects.map((user) => (
                <div
                  key={user.id}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium',
                    'bg-[color-mix(in_srgb,var(--color-2-nuance-1)_12%,transparent)]',
                    'text-[var(--color-2-nuance-1)]',
                    'border border-[var(--color-2-nuance-1)]/30',
                  )}
                >
                  <span
                    className={cn(
                      'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold',
                      'bg-[var(--color-2-nuance-1)] text-white',
                    )}
                  >
                    {getInitials(user.firstName, user.lastName)}
                  </span>
                  <span>
                    {user.firstName} {user.lastName}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeSelectedUser(user.id)}
                    className="p-0.5 hover:bg-[var(--color-2-nuance-1)]/20 rounded-full transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <SearchInput
            placeholder="Rechercher par nom ou email..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            variant="filled"
            size="md"
          />

          <div className="rounded-xl border border-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)] p-3">
            <div className="max-h-48 overflow-y-auto scrollbar-thin space-y-1">
              {(() => {
                if (isLoading) {
                  return (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[var(--color-2-nuance-1)]" />
                      <span className="ml-2 text-sm text-[var(--color-1-nuance-2)]">
                        Chargement...
                      </span>
                    </div>
                  );
                }

                if (users.length === 0) {
                  return (
                    <p className="text-center py-4 text-sm text-[var(--color-1-nuance-2)]/60">
                      Aucun utilisateur trouvé
                    </p>
                  );
                }

                return users.map((user) => {
                  const isAlreadyShared = alreadySharedIds.includes(user.id);
                  const isSelected = selectedUsers.includes(user.id) || isAlreadyShared;
                  return (
                    <button
                      type="button"
                      key={user.id}
                      onClick={() => toggleUserSelection(user)}
                      disabled={isAlreadyShared}
                      className={cn(
                        'w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-all duration-200',
                        isAlreadyShared ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                        isSelected && !isAlreadyShared
                          ? 'bg-[color-mix(in_srgb,var(--color-2-nuance-1)_8%,transparent)] border border-[var(--color-2-nuance-1)]/30'
                          : 'hover:bg-[color-mix(in_srgb,var(--color-1-nuance-3)_10%,transparent)] border border-[color-mix(in_srgb,var(--color-1-nuance-3)_20%,transparent)]',
                      )}
                    >
                      <ShadcnCheckbox
                        checked={isSelected}
                        disabled={isAlreadyShared}
                        className="pointer-events-none"
                      />
                      <div
                        className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold',
                          isSelected && !isAlreadyShared
                            ? 'bg-[var(--color-2-nuance-1)] text-white'
                            : 'bg-[color-mix(in_srgb,var(--color-3-nuance-1)_15%,transparent)] text-[var(--color-3-nuance-1)]',
                        )}
                      >
                        {getInitials(user.firstName, user.lastName)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={cn(
                            'text-sm font-medium truncate',
                            isSelected && !isAlreadyShared
                              ? 'text-[var(--color-2-nuance-1)]'
                              : 'text-[var(--color-1-nuance-1)]',
                          )}
                        >
                          {user.firstName} {user.lastName}
                          {isAlreadyShared ? ' (déjà partagé)' : ''}
                        </p>
                        <p className="text-xs text-[var(--color-1-nuance-2)]/70 truncate">
                          {user.email}
                        </p>
                      </div>
                    </button>
                  );
                });
              })()}
            </div>
          </div>

          {!isLoading && totalPages > 1 && (
            <div className="flex items-center justify-between pt-2 border-t border-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)]">
              <div className="text-sm text-[var(--color-1-nuance-2)]">
                Page {page} sur {totalPages} ({total} utilisateur{total > 1 ? 's' : ''})
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => page > 1 && setPage((p) => p - 1)}
                  disabled={page === 1}
                  icon={ChevronLeft}
                  title="Page précédente"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => page < totalPages && setPage((p) => p + 1)}
                  disabled={page === totalPages}
                  icon={ChevronRight}
                  title="Page suivante"
                />
              </div>
            </div>
          )}

          {!isLoading && users.length > 0 && (
            <Button
              type="button"
              variant="custom"
              onClick={handleSelectAll}
              icon={
                users
                  .filter((u) => !alreadySharedIds.includes(u.id))
                  .every((u) => selectedUsers.includes(u.id))
                  ? X
                  : Check
              }
              className={cn(
                'w-full py-2 text-sm font-medium rounded-lg transition-colors',
                'bg-[color-mix(in_srgb,var(--color-1-nuance-3)_10%,transparent)]',
                'hover:bg-[color-mix(in_srgb,var(--color-1-nuance-3)_20%,transparent)]',
                'text-[var(--color-1-nuance-1)]',
              )}
            >
              {users
                .filter((u) => !alreadySharedIds.includes(u.id))
                .every((u) => selectedUsers.includes(u.id))
                ? 'Tout désélectionner'
                : 'Tout sélectionner'}
            </Button>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm text-[var(--color-1-nuance-1)]">Permission :</span>
            <SegmentedControl<'read' | 'write'>
              value={permission}
              onChange={setPermission}
              options={[
                { value: 'read', label: 'Lecture', icon: <BookOpen className="h-4 w-4" /> },
                { value: 'write', label: 'Écriture', icon: <Edit className="h-4 w-4" /> },
              ]}
              size="md"
            />
          </div>

          {shares.length > 0 && (
            <div className="pt-3 border-t border-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)]">
              <h4 className="text-xs font-semibold text-[var(--color-1-nuance-2)] uppercase tracking-wider mb-2">
                Déjà partagé avec ({shares.length})
              </h4>
              <div className="space-y-1.5">
                {shares.map((share) => (
                  <div
                    key={share.id}
                    className={cn(
                      'flex items-center justify-between p-2 rounded-lg',
                      'bg-[color-mix(in_srgb,var(--color-3-nuance-1)_8%,transparent)]',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-[var(--color-3-nuance-1)] text-white flex items-center justify-center text-[10px] font-bold">
                        {share.userName
                          ?.split(' ')
                          .map((n) => n[0])
                          .join('')
                          .toUpperCase() || '?'}
                      </div>
                      <span className="text-sm text-[var(--color-1-nuance-1)]">
                        {share.userName}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span
                        className={cn(
                          'text-xs px-1.5 py-0.5 rounded',
                          'bg-[color-mix(in_srgb,var(--color-3-nuance-1)_15%,transparent)]',
                          'text-[var(--color-3-nuance-1)]',
                        )}
                      >
                        {share.permission === 'write' ? 'Écriture' : 'Lecture'}
                      </span>
                      {canRemoveExisting && (
                        <button
                          type="button"
                          onClick={() => handleRemoveShare(share.userId)}
                          className="p-1 rounded hover:bg-destructive/10 text-destructive transition-colors"
                          title="Retirer l'accès"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 flex gap-2 px-6 py-4 border-t border-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)] bg-background">
          <Button
            type="button"
            variant="primary"
            onClick={handleShareUsers}
            disabled={(!deferUntilSave && selectedUsers.length === 0) || isSharing}
            loading={isSharing}
            className="flex-1"
            icon={Share2}
          >
            {(() => {
              if (isSharing) {
                return deferUntilSave ? 'Enregistrement...' : 'Partage en cours...';
              }
              if (deferUntilSave) {
                return selectedUsers.length > 0
                  ? `Confirmer (${selectedUsers.length})`
                  : 'Confirmer la sélection';
              }
              return `Partager (${selectedUsers.length})`;
            })()}
          </Button>
          <Button type="button" preset="modal-cancel" onClick={onClose} disabled={isSharing}>
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
